/**
 * The shell: a collapsible rail, a top bar, and the routed content.
 *
 * The rail collapses to 56px because the module screens are wide data grids — the
 * Accounting table carries 17 columns and the OCR review screen is a side-by-side
 * image viewer. Collapsing gives that width back (measured: 1344px of content on a
 * 1400px viewport), which is also why module tabs are full-page navigations rather
 * than iframes.
 *
 * The Accounting grid no longer BREAKS below a given width — it is table-layout:fixed
 * and sheds columns in tiers (17 above 1340px, 13 below, 8 below 980px, 4 below
 * 700px), so it fits anything and never scrolls sideways. Collapsing the rail
 * therefore buys COLUMNS rather than usability: at 1400px it is the difference
 * between the 13-column and the 17-column layout. Earlier revisions of this comment
 * said the grid "needs ~1220px"; that stopped being true when the tiers landed.
 *
 * Navigation is rendered from the server's module list, so it already reflects the
 * caller's permissions. That is presentation only — the gateway refuses the path
 * independently.
 */

import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { useI18n } from "../shared/i18n";
import { Icon } from "../shared/ui/icons";
import { LangToggle } from "../shared/ui/LangToggle";
import { ThemeToggle } from "../shared/ui/ThemeToggle";
import { useSession } from "../features/auth/session";

const RAIL_KEY = "petpoint-rail-collapsed";

export function AppShell() {
  const { session, signOut } = useSession();
  const { t, lang } = useI18n();
  const location = useLocation();
  // A module tab is a full-page navigation: React unmounts, the browser keeps showing
  // this page, and the new document paints whenever it is ready. Without feedback the
  // click looks ignored and people click twice. Cleared by the navigation itself.
  const [leaving, setLeaving] = useState(false);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === "1";
    } catch {
      return false;
    }
  });

  function toggleRail() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(RAIL_KEY, next ? "1" : "0");
    } catch {
      /* private mode */
    }
  }

  const modules = session?.modules ?? [];

  /** The server supplies both names; fall back to English if Arabic is blank. */
  const label = (m: { name: string; name_ar: string }) =>
    lang === "ar" && m.name_ar ? m.name_ar : m.name;

  // Longest matching prefix, not an exact match: /admin/roles and /admin/audit are
  // both the Administration module, and an exact match left the title reading
  // "PetPoint" on every admin sub-page. "/" is excluded because it prefixes all.
  const current =
    modules.find((m) => m.base_path === location.pathname) ??
    [...modules]
      .filter((m) => m.base_path !== "/" && location.pathname.startsWith(m.base_path))
      .sort((a, b) => b.base_path.length - a.base_path.length)[0];

  return (
    <div className={`shell ${collapsed ? "collapsed" : ""}`}>
      {leaving && <div className="nav-progress" role="progressbar" aria-label={t("nav.loading")} />}
      <nav className="rail" aria-label={t("nav.modules")}>
        <div className="rail-brand">
          {/* The full lockup when there is room, the mark alone when there is not.
              Both are the client's own files; mark.png exists for exactly this. */}
          <img
            src={collapsed ? "/brand/mark.png" : "/brand/logo.png"}
            alt={t("app.name")}
            className={collapsed ? "rail-mark" : "rail-logo"}
          />
        </div>

        <ul className="rail-nav">
          {modules.map((m) => (
            <li key={m.key}>
              {/* A module served by the gateway is a real navigation, not a
                  client-side route: its pages are server-rendered by that service.
                  The dashboard and admin are React routes. */}
              {m.base_path.startsWith("/apps/") ? (
                <a href={m.base_path} title={label(m)} onClick={() => setLeaving(true)}>
                  <Icon name={m.icon} size={22} />
                  <span className="rail-label">{label(m)}</span>
                </a>
              ) : (
                <NavLink to={m.base_path} title={label(m)} end={m.base_path === "/"}>
                  <Icon name={m.icon} size={22} />
                  <span className="rail-label">{label(m)}</span>
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="rail-collapse"
          onClick={toggleRail}
          aria-expanded={!collapsed}
          title={collapsed ? t("nav.expand") : t("nav.collapse")}
        >
          <Icon name="chevron" size={18} />
          <span className="sr-only">{collapsed ? t("nav.expand") : t("nav.collapse")}</span>
        </button>
      </nav>

      <div className="main">
        <header className="topbar">
          <h1>{current ? label(current) : t("app.name")}</h1>
          <div className="topbar-right">
            <LangToggle />
            <ThemeToggle />
            <span className="who" title={session?.user.email}>
              {session?.user.full_name}
            </span>
            <button type="button" className="btn ghost" onClick={() => void signOut()}>
              <Icon name="logout" size={17} />
              <span>{t("nav.signOut")}</span>
            </button>
          </div>
        </header>

        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
